import { useEffect, useRef } from "react";
import L from "leaflet";

/**
 * Satellite property preview using free Esri World Imagery tiles (no API key).
 * Grayscale filter applied when `unavailable` (per design spec).
 * When `draggable` is true, the pin can be dragged and `onPinMove(lat, lng)` fires on drop.
 */
export function MapPreview({
  lat,
  lng,
  unavailable = false,
  draggable = false,
  onPinMove,
  height = 240,
  testid = "map-preview",
}) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const moveCbRef = useRef(onPinMove);
  moveCbRef.current = onPinMove;

  useEffect(() => {
    if (!ref.current || lat == null || lng == null) return;
    let raf;
    if (!mapRef.current) {
      const map = L.map(ref.current, {
        center: [lat, lng],
        zoom: 19,
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: false,
        dragging: true,
      });
      L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 21, attribution: "Imagery © Esri" }
      ).addTo(map);
      const icon = L.divIcon({
        className: "",
        html: '<div style="width:18px;height:18px;border-radius:50%;background:#E07B2A;border:3px solid #fff;box-shadow:0 0 0 2px rgba(0,0,0,.25)"></div>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      const marker = L.marker([lat, lng], { icon, draggable }).addTo(map);
      marker.on("dragend", () => {
        const p = marker.getLatLng();
        moveCbRef.current?.(p.lat, p.lng);
      });
      markerRef.current = marker;
      mapRef.current = map;
      // requestAnimationFrame avoids the Leaflet "_leaflet_pos" race on first layout.
      raf = requestAnimationFrame(() => mapRef.current && mapRef.current.invalidateSize());
    } else {
      mapRef.current.invalidateSize();
      mapRef.current.setView([lat, lng], mapRef.current.getZoom());
      markerRef.current?.setLatLng([lat, lng]);
    }
    return () => raf && cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  // Toggle draggability without recreating the marker.
  useEffect(() => {
    const m = markerRef.current;
    if (!m || !m.dragging) return;
    if (draggable) m.dragging.enable();
    else m.dragging.disable();
  }, [draggable]);

  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    },
    []
  );

  return (
    <div
      data-testid={testid}
      className={`w-full rounded-input overflow-hidden border-2 border-gravel ${draggable ? "cursor-grab" : ""} ${unavailable ? "map-grayscale" : ""}`}
      style={{ height }}
    >
      {/* Leaflet owns this inner node; its className stays static so React never clobbers
          the leaflet-* classes Leaflet adds imperatively (which breaks tile rendering). */}
      <div
        ref={ref}
        className="w-full h-full"
        role="img"
        aria-label="Satellite view of the selected property"
      />
    </div>
  );
}

export default MapPreview;
